import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import Header from "../../components/Header";
import api from "../../utils/api";
import { validateFile } from "../../utils/validation";
import { getProfileImageUrl } from "../../utils/imageUtils";
import { reverseGeocode } from "../../utils/locationUtils";
import { usePopup } from "../../context/PopupContext";
import ProfileImageCropModal from "../../components/ProfileImageCropModal";
import { FaCamera, FaMapMarkerAlt } from "react-icons/fa";
import "./ConsumerProfile.css";

const ConsumerProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast, showConfirm } = usePopup();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [, setIsEditingProfile] = useState(false);
  const [, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [exportingData, setExportingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    district: "",
    province: "",
    dateOfBirth: "",
    gender: "",
  });

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "consumer") {
          navigate("/");
          return;
        }
        fetchProfile();
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get("/users/profile");
      const profileData = response.data;

      const userData = JSON.parse(localStorage.getItem("user"));
      setFormData({
        username: profileData.username || userData?.username || "",
        name: profileData.name || "",
        email: profileData.email || "",
        phone: profileData.phone || "",
        address: profileData.address?.street || profileData.address || "",
        city: profileData.address?.city || "",
        district: profileData.address?.district || "",
        province: profileData.address?.province || "",
        dateOfBirth: profileData.dateOfBirth || "",
        gender: profileData.gender || "",
      });

      // Update user in state and localStorage with all profile data including profileImage
      const updatedUserData = {
        ...userData,
        ...profileData,
        profileImage: profileData.profileImage || userData?.profileImage,
      };
      localStorage.setItem("user", JSON.stringify(updatedUserData));
      setUser(updatedUserData);
    } catch (error) {
      console.error("Error fetching profile:", error);
      // Use localStorage data as fallback
      const userObj = JSON.parse(localStorage.getItem("user"));
      setFormData({
        username: userObj.username || "",
        name: userObj.name || "",
        email: userObj.email || "",
        phone: userObj.phone || "",
        address: "",
        city: "",
        district: "",
        province: "",
        dateOfBirth: "",
        gender: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.address,
          city: formData.city,
          district: formData.district,
          province: formData.province,
        },
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
      };

      const response = await api.put("/users/profile", updateData);

      // Update user in localStorage
      const userData = JSON.parse(localStorage.getItem("user"));
      userData.name = response.data.name;
      userData.email = response.data.email;
      userData.phone = response.data.phone;
      if (response.data.profileImage) {
        userData.profileImage = response.data.profileImage;
      }
      if (response.data.gender !== undefined) {
        userData.gender = response.data.gender;
      }
      if (response.data.dateOfBirth !== undefined) {
        userData.dateOfBirth = response.data.dateOfBirth;
      }
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);

      // Trigger storage event so Header updates
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("userUpdated"));

      setEditMode(false);
      showToast(t("consumerProfile.profileUpdated"), "success");
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        t("consumerProfile.profileUpdateError");
      showToast(errorMessage, "error");
    }
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    try {
      const updateData = {
        address: {
          street: formData.address,
          city: formData.city,
          district: formData.district,
          province: formData.province,
        },
      };
      const response = await api.put("/users/profile", updateData);
      const userData = JSON.parse(localStorage.getItem("user"));
      const updated = { ...userData, ...response.data };
      localStorage.setItem("user", JSON.stringify(updated));
      setUser(updated);
      window.dispatchEvent(new Event("userUpdated"));
      setEditingAddress(false);
      showToast(t("consumerProfile.addressUpdated"), "success");
    } catch (error) {
      showToast(
        error.response?.data?.message ||
          t("consumerProfile.addressUpdateError"),
        "error",
      );
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = () => {
    showConfirm({
      title: t("consumerProfile.deleteAddressTitle"),
      message: t("consumerProfile.deleteAddressMessage"),
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        setSavingAddress(true);
        try {
          const updateData = {
            address: { street: "", city: "", district: "", province: "" },
          };
          await api.put("/users/profile", updateData);
          setFormData((prev) => ({
            ...prev,
            address: "",
            city: "",
            district: "",
            province: "",
          }));
          const userData = JSON.parse(localStorage.getItem("user"));
          const updated = {
            ...userData,
            address: { street: "", city: "", district: "", province: "" },
          };
          localStorage.setItem("user", JSON.stringify(updated));
          setUser(updated);
          window.dispatchEvent(new Event("userUpdated"));
          setEditingAddress(false);
          showToast(t("consumerProfile.addressDeleted"), "success");
        } catch (error) {
          showToast(
            error.response?.data?.message ||
              t("consumerProfile.addressDeleteError"),
            "error",
          );
        } finally {
          setSavingAddress(false);
        }
      },
    });
  };

  const hasAddress =
    formData.address || formData.city || formData.district || formData.province;

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      showToast(t("consumerProfile.geolocationNotSupported"), "error");
      return;
    }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const geo = await reverseGeocode(latitude, longitude);
          setFormData((prev) => ({
            ...prev,
            address: geo.address || prev.address,
            city: geo.city || geo.municipality || prev.city,
            district: geo.district || prev.district,
            province: geo.province || prev.province,
          }));
          showToast(t("consumerProfile.locationDetected"), "success");
        } catch (err) {
          showToast(t("consumerProfile.locationDetectError"), "error");
        } finally {
          setDetectingLocation(false);
        }
      },
      () => {
        setDetectingLocation(false);
        showToast(t("consumerProfile.locationPermissionDenied"), "error");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file, [
      "image/png",
      "image/jpeg",
      "image/jpg",
    ]);
    if (!validation.isValid) {
      showToast(validation.error, "error");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCropImageSrc(reader.result);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const uploadProfileImageFile = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await api.put("/users/profile", formData);

      const userData = JSON.parse(localStorage.getItem("user"));
      if (response.data.profileImage) {
        userData.profileImage = response.data.profileImage;
        localStorage.setItem("user", JSON.stringify(userData));
        setUser({ ...userData });
        setImageKey((prev) => prev + 1);
      }

      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("userUpdated"));

      setProfileImageFile(null);
      setProfileImagePreview(null);
      setIsEditingProfile(false);
      showToast(t("consumerProfile.profilePictureUpdated"), "success");
    } catch (error) {
      console.error("Error uploading profile image:", error);
      showToast(
        error.response?.data?.message ||
          t("consumerProfile.profilePictureError"),
        "error",
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCropSave = (blob) => {
    const file = new File([blob], "profile.jpg", { type: "image/jpeg" });
    setCropModalOpen(false);
    setCropImageSrc(null);
    uploadProfileImageFile(file);
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
    setIsEditingProfile(false);
  };

  const getProfileImage = () => {
    if (profileImagePreview) {
      return profileImagePreview;
    }
    if (user?.profileImage) {
      // Add cache-busting parameter to force image refresh after update
      const cacheBust = imageKey > 0;
      if (user.profileImage.startsWith("http")) {
        return cacheBust
          ? `${user.profileImage}?t=${Date.now()}`
          : user.profileImage;
      }
      return getProfileImageUrl(user.profileImage, cacheBust);
    }
    return "/images/deafultpic.png";
  };

  const downloadBackupFile = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `taja-bazzar-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBackup = async () => {
    setExportingData(true);
    try {
      const { data } = await api.get("/users/export-data");
      downloadBackupFile(data);
      showToast(t("consumerProfile.backupDownloaded"), "success");
    } catch (err) {
      showToast(
        err.response?.data?.message || t("consumerProfile.exportDataError"),
        "error",
      );
    } finally {
      setExportingData(false);
    }
  };

  const uploadBackupToDrive = async (googleAccessToken) => {
    setExportingData(true);
    try {
      const { data } = await api.post("/users/backup-to-drive", {
        googleAccessToken,
      });
      showToast(data.message || t("consumerProfile.backupUploaded"), "success");
      if (data.webViewLink) {
        window.open(data.webViewLink, "_blank");
      }
    } catch (err) {
      showToast(
        err.response?.data?.message || t("consumerProfile.backupUploadError"),
        "error",
      );
    } finally {
      setExportingData(false);
    }
  };

  const googleDriveLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/drive.file",
    onSuccess: (tokenResponse) => {
      uploadBackupToDrive(tokenResponse.access_token);
    },
    onError: () => {
      setExportingData(false);
      showToast(t("consumerProfile.googleSignInError"), "error");
    },
  });

  const handleBackupToGoogleDrive = () => {
    setExportingData(true);
    googleDriveLogin();
  };

  const handleDeleteAccount = () => {
    showConfirm({
      title: t("consumerProfile.backupDataFirstTitle"),
      message: t("consumerProfile.backupDataFirstMessage"),
      confirmLabel: t("consumerProfile.yesContinueDelete"),
      cancelLabel: t("consumerProfile.noBackupFirst"),
      onConfirm: () => {
        showConfirm({
          title: t("consumerProfile.deleteAccountTitle"),
          message: t("consumerProfile.deleteAccountMessage"),
          confirmLabel: "Delete my account",
          cancelLabel: "Cancel",
          onConfirm: async () => {
            setDeletingAccount(true);
            try {
              await api.delete("/users/account");
              showToast(t("consumerProfile.accountDeleted"), "success");
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              navigate("/");
              window.location.reload();
            } catch (err) {
              showToast(
                err.response?.data?.message ||
                  t("consumerProfile.deleteAccountError"),
                "error",
              );
            } finally {
              setDeletingAccount(false);
            }
          },
        });
      },
    });
  };

  if (loading) {
    return (
      <div className="consumer-profile-page">
        <Header />
        <div className="consumer-profile-loading-state">
          <div className="consumer-profile-spinner"></div>
          <p>{t("consumerProfile.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="consumer-profile-page">
      <Header />

      <div className="consumer-profile-container">
        <div className="consumer-profile-layout">
          {/* Profile Header - Left Side */}
          <div className="consumer-profile-header">
            <div className="consumer-profile-avatar-section">
              <div className="consumer-profile-avatar-wrapper">
                <img
                  key={`profile-avatar-${imageKey}`}
                  src={getProfileImage()}
                  alt={formData.name}
                  className="consumer-profile-avatar-large"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/deafultpic.png";
                  }}
                />
                {uploadingImage && (
                  <div className="consumer-profile-uploading-overlay">
                    {t("consumerProfile.uploading")}
                  </div>
                )}
                <div className="consumer-profile-avatar-edit-overlay">
                  <input
                    type="file"
                    id="consumerProfileImageInput"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(e) => {
                      handleProfileImageChange(e);
                      setIsEditingProfile(true);
                    }}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="consumerProfileImageInput"
                    className="consumer-profile-camera-icon-btn"
                    title={t("consumerProfile.changePhoto")}
                  >
                    <FaCamera />
                  </label>
                </div>
              </div>
            </div>
            <div className="consumer-profile-info-header">
              <h1 className="consumer-profile-name">{formData.name}</h1>
              {formData.username && (
                <p className="consumer-profile-username">
                  @{formData.username}
                </p>
              )}
              <p className="consumer-profile-email">{formData.email}</p>
              <p className="consumer-profile-location">
                <FaMapMarkerAlt
                  className="consumer-profile-location-icon"
                  aria-hidden
                />
                {formData.district}, {formData.province}
              </p>
            </div>
          </div>

          {/* Tabs and Content - Right Side */}
          <div className="consumer-profile-right-section">
            {/* Tabs */}
            <div className="consumer-profile-tabs">
              <button
                className={`consumer-profile-tab-btn ${activeTab === "profile" ? "active" : ""}`}
                onClick={() => setActiveTab("profile")}
              >
                {t("consumerProfile.profileTab")}
              </button>
              <button
                className={`consumer-profile-tab-btn ${activeTab === "addresses" ? "active" : ""}`}
                onClick={() => setActiveTab("addresses")}
              >
                {t("consumerProfile.addressesTab")}
              </button>
              <button
                className={`consumer-profile-tab-btn ${activeTab === "settings" ? "active" : ""}`}
                onClick={() => setActiveTab("settings")}
              >
                {t("consumerProfile.settingsTab")}
              </button>
            </div>

            {/* Tab Content */}
            <div className="consumer-profile-content">
              {activeTab === "profile" && (
                <div className="consumer-profile-tab-content">
                  <div className="consumer-profile-tab-header">
                    <h2>{t("consumerProfile.personalInfo")}</h2>
                    {!editMode && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="consumer-profile-btn-edit"
                      >
                        {t("consumerProfile.editProfile")}
                      </button>
                    )}
                  </div>

                  <form className="consumer-profile-form">
                    <div className="consumer-profile-form-row">
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.fullName")}</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          disabled={!editMode}
                        />
                      </div>
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.username")}</label>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <input
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={(e) => {
                              const value = e.target.value
                                .toLowerCase()
                                .replace(/\s/g, "");
                              setFormData({
                                ...formData,
                                username: value,
                              });
                            }}
                            disabled={!editMode}
                            maxLength="30"
                            style={{
                              opacity: !editMode ? 0.6 : 1,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="consumer-profile-form-row">
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.email")}</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={!editMode}
                        />
                      </div>
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.phoneNumber")}</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={!editMode}
                        />
                      </div>
                    </div>

                    <div className="consumer-profile-form-row">
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.dateOfBirth")}</label>
                        <input
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          disabled={!editMode}
                        />
                      </div>
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.gender")}</label>
                        <select
                          name="gender"
                          value={formData.gender}
                          onChange={handleChange}
                          disabled={!editMode}
                        >
                          <option value="">
                            {t("consumerProfile.select")}
                          </option>
                          <option value="male">
                            {t("consumerProfile.male")}
                          </option>
                          <option value="female">
                            {t("consumerProfile.female")}
                          </option>
                          <option value="other">
                            {t("consumerProfile.other")}
                          </option>
                        </select>
                      </div>
                    </div>

                    {editMode && (
                      <div className="consumer-profile-form-actions">
                        <button
                          type="button"
                          onClick={() => setEditMode(false)}
                          className="consumer-profile-btn-cancel"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSave}
                          className="consumer-profile-btn-save"
                        >
                          {t("consumerProfile.saveChanges")}
                        </button>
                      </div>
                    )}
                  </form>
                </div>
              )}

              {activeTab === "addresses" && (
                <div className="consumer-profile-tab-content">
                  <div className="consumer-profile-tab-header">
                    <h2>{t("consumerProfile.savedAddresses")}</h2>
                    {!editingAddress && !hasAddress && (
                      <button
                        className="consumer-profile-btn-add-address"
                        onClick={() => setEditingAddress(true)}
                      >
                        {t("consumerProfile.addNewAddress")}
                      </button>
                    )}
                  </div>

                  {editingAddress ? (
                    <div className="consumer-profile-address-form">
                      <button
                        type="button"
                        className="consumer-profile-btn-detect-location"
                        onClick={handleDetectLocation}
                        disabled={detectingLocation}
                      >
                        {detectingLocation ? (
                          <>
                            <span className="consumer-profile-detect-spinner"></span>{" "}
                            {t("consumerProfile.detecting")}
                          </>
                        ) : (
                          <>
                            <FaMapMarkerAlt
                              className="consumer-profile-detect-icon"
                              aria-hidden
                            />{" "}
                            {t("consumerProfile.useCurrentLocation")}
                          </>
                        )}
                      </button>
                      <div className="consumer-profile-form-row">
                        <div className="consumer-profile-form-group">
                          <label>{t("consumerProfile.streetAddress")}</label>
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder={t("consumerProfile.streetPlaceholder")}
                          />
                        </div>
                        <div className="consumer-profile-form-group">
                          <label>{t("consumerProfile.city")}</label>
                          <input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder={t("consumerProfile.cityPlaceholder")}
                          />
                        </div>
                      </div>
                      <div className="consumer-profile-form-row">
                        <div className="consumer-profile-form-group">
                          <label>{t("consumerProfile.district")}</label>
                          <input
                            type="text"
                            name="district"
                            value={formData.district}
                            onChange={handleChange}
                            placeholder={t(
                              "consumerProfile.districtPlaceholder",
                            )}
                          />
                        </div>
                        <div className="consumer-profile-form-group">
                          <label>{t("consumerProfile.province")}</label>
                          <select
                            name="province"
                            value={formData.province}
                            onChange={handleChange}
                          >
                            <option value="">
                              {t("consumerProfile.selectProvince")}
                            </option>
                            <option value="Koshi">Koshi</option>
                            <option value="Madhesh">Madhesh</option>
                            <option value="Bagmati">Bagmati</option>
                            <option value="Gandaki">Gandaki</option>
                            <option value="Lumbini">Lumbini</option>
                            <option value="Karnali">Karnali</option>
                            <option value="Sudurpashchim">Sudurpashchim</option>
                          </select>
                        </div>
                      </div>
                      <div className="consumer-profile-form-actions">
                        <button
                          type="button"
                          className="consumer-profile-btn-cancel"
                          onClick={() => {
                            setEditingAddress(false);
                            fetchProfile();
                          }}
                          disabled={savingAddress}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="consumer-profile-btn-save"
                          onClick={handleSaveAddress}
                          disabled={savingAddress}
                        >
                          {savingAddress
                            ? t("consumerProfile.saving")
                            : t("consumerProfile.saveAddress")}
                        </button>
                      </div>
                    </div>
                  ) : hasAddress ? (
                    <div className="consumer-profile-addresses-list">
                      <div className="consumer-profile-address-card default">
                        <div className="consumer-profile-address-header">
                          <h3>{t("consumerProfile.homeAddress")}</h3>
                          <span className="consumer-profile-default-badge">
                            {t("consumerProfile.default")}
                          </span>
                        </div>
                        {formData.address && <p>{formData.address}</p>}
                        <p>
                          {[formData.city, formData.district, formData.province]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        <div className="consumer-profile-address-actions">
                          <button
                            className="consumer-profile-btn-edit-address"
                            onClick={() => setEditingAddress(true)}
                          >
                            Edit
                          </button>
                          <button
                            className="consumer-profile-btn-delete-address"
                            onClick={handleDeleteAddress}
                            disabled={savingAddress}
                          >
                            {savingAddress
                              ? t("consumerProfile.deleting")
                              : "Delete"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="consumer-profile-empty-address">
                      <p>{t("consumerProfile.noAddressSaved")}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "settings" && (
                <div className="consumer-profile-tab-content">
                  <h2>{t("consumerProfile.accountSettings")}</h2>
                  <div className="consumer-profile-settings-section">
                    <h3>{t("consumerProfile.changePassword")}</h3>
                    <div className="consumer-profile-settings-form">
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.currentPassword")}</label>
                        <input
                          type="password"
                          placeholder={t(
                            "consumerProfile.currentPasswordPlaceholder",
                          )}
                        />
                      </div>
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.newPassword")}</label>
                        <input
                          type="password"
                          placeholder={t(
                            "consumerProfile.newPasswordPlaceholder",
                          )}
                        />
                      </div>
                      <div className="consumer-profile-form-group">
                        <label>{t("consumerProfile.confirmNewPassword")}</label>
                        <input
                          type="password"
                          placeholder={t(
                            "consumerProfile.confirmNewPasswordPlaceholder",
                          )}
                        />
                      </div>
                      <button className="consumer-profile-btn-change-password">
                        {t("consumerProfile.changePassword")}
                      </button>
                    </div>
                  </div>
                  <div className="consumer-profile-settings-section">
                    <h3>{t("consumerProfile.notificationPreferences")}</h3>
                    <div className="consumer-profile-settings-checkboxes">
                      <label className="consumer-profile-checkbox-label">
                        <input type="checkbox" defaultChecked />
                        <span>{t("consumerProfile.emailNotifications")}</span>
                      </label>
                      <label className="consumer-profile-checkbox-label">
                        <input type="checkbox" defaultChecked />
                        <span>{t("consumerProfile.smsNotifications")}</span>
                      </label>
                      <label className="consumer-profile-checkbox-label">
                        <input type="checkbox" />
                        <span>{t("consumerProfile.marketingEmails")}</span>
                      </label>
                    </div>
                  </div>
                  <div className="consumer-profile-settings-section consumer-profile-settings-danger">
                    <h3>{t("consumerProfile.backupDeleteAccount")}</h3>
                    <p className="consumer-profile-settings-hint">
                      {t("consumerProfile.backupHint")}
                    </p>
                    <div className="consumer-profile-settings-actions">
                      <button
                        type="button"
                        className="consumer-profile-btn-backup"
                        onClick={handleDownloadBackup}
                        disabled={exportingData}
                      >
                        {exportingData
                          ? t("consumerProfile.preparing")
                          : t("consumerProfile.downloadMyData")}
                      </button>
                      <button
                        type="button"
                        className="consumer-profile-btn-backup-drive"
                        onClick={handleBackupToGoogleDrive}
                        disabled={exportingData}
                      >
                        {exportingData
                          ? t("consumerProfile.preparing")
                          : t("consumerProfile.backupToGoogleDrive")}
                      </button>
                      <button
                        type="button"
                        className="consumer-profile-btn-delete-account"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                      >
                        {deletingAccount
                          ? t("consumerProfile.deletingAccount")
                          : t("consumerProfile.deleteMyAccount")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <ProfileImageCropModal
          imageSrc={cropImageSrc}
          open={cropModalOpen}
          onSave={handleCropSave}
          onCancel={handleCropCancel}
        />
      </div>
    </div>
  );
};

export default ConsumerProfile;
